// @ts-nocheck

import React, { useMemo, useRef } from 'react';
import { useState, useCallback } from 'react';
import type { Node } from 'react';
import type { ElementRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { StreamModes } from '../../constants/picture-in-picture';
import PictureInPictureContext from '../../contexts/PictureInPictureContext';
import { isAndroid } from '../../lib/useragent';
import isHome from '../../utils/routes/isHome';
import isMatch, { getMatchId } from '../../utils/routes/isMatch';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';

/* DEBUG: to see custom floating component: change temporary isPipEnabled to !isPipEnabled  */
const isPipEnabled = !process.env.REACT_APP_LIB_ENV && 'pictureInPictureEnabled' in document && !isAndroid;

type Props = {
  children: Node;
  showWTV?: boolean;
  isWTVEnabled?: boolean;
  onMatchStreamExport?: () => void;
};

/**
 * @param {Node} children all the necessary node elements we need
 *
 * @return {Node} the provider with the children
 */
const PiPProvider = ({ showWTV = false, isWTVEnabled = false, onMatchStreamExport, children }: Props) => {
  const [isPipCustomActive, setPipCustomIsActive] = useState(false);
  const [isPipNativeActive, setPipNativeActive] = useState(false);
  const [askRestore, setAskRestore] = useState(false);
  const [askPip, setAskPip] = useState(false);
  const [askWtv, setAskWtv] = useState(false);
  const [stickyOffsetTop, setStickyOffsetTop] = useState(null); // placeholder offset top when we are on sticky mode, null if not on sticky mode
  const [type, setType] = useState(StreamModes.MATCH);
  const [matchId, setMatchId] = useState(null);
  const [streamData, setStreamData] = useState(null);
  const [jwt, setJwt] = useState(null);
  const [placeholderRef, setPlaceholderRef] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSameMatchRouteAndPipNativeActive, setIsSameMatchRouteAndPipNativeActive] = useState(false);
  const floatingVideoRef = useRef<ElementRef<'div'> | null>(null);
  const videoDomNodeRef = useRef();

  const isPipClicked = useCallback((target) => {
    const floatingVideo = floatingVideoRef.current;
    if (!floatingVideo) {
      return false;
    }

    // @see compatibility: https://developer.mozilla.org/fr/docs/Web/API/Node/contains
    return floatingVideo.contains(target);
  }, []);

  const location = useLocation();
  const history = useHistory();

  const isRouteMatch = isMatch(location.pathname);
  const paramMatchId = getMatchId(location.pathname);
  const isMatchRouteRelativeToStream = isRouteMatch && paramMatchId === matchId;

  const getStreamWtvOpen = useCallback(() => {
    // case 1: if we are on a Match stream
    // case 2: if the WTV user preference is false
    // case 3: if the WTV is disabled from the Back office
    if (type !== StreamModes.TV || !showWTV || !isWTVEnabled) {
      return false;
    }

    // case 1: if WTV is activated + Home route
    // case 2: if WTV is activated + PiP mode + not on Home route
    // eslint-disable-next-line sonarjs/prefer-single-boolean-return
    if (isHome(location.pathname) || isPipCustomActive) {
      return true;
    }

    return false;
  }, [isPipCustomActive, isWTVEnabled, location.pathname, showWTV, type]);

  const closeCurrentNativePip = async () => {
    // if PiP is already active, leave it otherwise request it
    // @see https://developer.mozilla.org/en-US/docs/Web/API/Document/pictureInPictureElement
    if (isPipEnabled && document.pictureInPictureElement && document.exitPictureInPicture) {
      try {
        await document.exitPictureInPicture();
        setPipNativeActive(false);
      } catch (e) {
        console.error(e);
      }
    }
  };

  // 1 - Request the native Pip if it is enabled
  // 2 - toggle the `isPipCustomActive` state
  const setPip = useCallback(async () => {
    if (isPipEnabled) {
      try {
        if (videoDomNodeRef.current) {
          await videoDomNodeRef.current.requestPictureInPicture();

          const onLeavePictureInPicture = () => {
            if (floatingVideoRef.current) {
              floatingVideoRef.current.style.removeProperty('z-index');
            }

            setPipNativeActive(false);
            videoDomNodeRef?.current.removeEventListener('leavepictureinpicture', onLeavePictureInPicture);
          };

          videoDomNodeRef.current.addEventListener('leavepictureinpicture', onLeavePictureInPicture);

          setPipNativeActive(true);
        }
      } catch (e) {
        console.log(e);
      }
    }
    setPipCustomIsActive((p) => !p);
  }, []);

  // mecanism to start correctly a match stream or a WTV stream
  const startStream = useCallback(async ({ id, type: newType }) => {
    await closeCurrentNativePip();
    if (newType === StreamModes.MATCH) {
      setMatchId(id);
    }
    setType(newType);
    setPipCustomIsActive(false);
  }, []);

  // mecanism to close a match stream or a WTV stream
  const closeStream = useCallback(async () => {
    await closeCurrentNativePip();
    if (type === StreamModes.MATCH) {
      setMatchId(null);
    }

    setPipCustomIsActive(false);
  }, [type]);

  // mecanism to restore a picture in picture stream to its default position
  const restoreStream = useCallback(() => {
    if (!isMatchRouteRelativeToStream && matchId) {
      history.push(RoutesRedirectionFactory.getMatchRouteConfig(matchId));
    }
    setPipCustomIsActive(false);
    setAskRestore(true);
  }, [history, matchId, isMatchRouteRelativeToStream]);

  const value = useMemo(
    () => ({
      askRestore,
      askPip,
      askWtv,
      closeStream,
      floatingVideoRef,
      getStreamWtvOpen,
      isFullscreen,
      isMatchRouteRelativeToStream,
      isPipClicked,
      isPipCustomActive,
      isPipEnabled,
      isPipNativeActive,
      isSameMatchRouteAndPipNativeActive,
      isWTVEnabled,
      jwt,
      matchId,
      placeholderRef,
      restoreStream,
      setAskRestore,
      setAskPip,
      setAskWtv,
      setIsFullscreen,
      setIsSameMatchRouteAndPipNativeActive,
      setJwt,
      setPip,
      setPipCustomIsActive,
      setPlaceholderRef,
      setStickyOffsetTop,
      setStreamData,
      startStream,
      stickyOffsetTop,
      streamData,
      type,
      videoDomNodeRef,
      onMatchStreamExport,
    }),
    [
      askPip,
      askRestore,
      askWtv,
      closeStream,
      getStreamWtvOpen,
      isFullscreen,
      isMatchRouteRelativeToStream,
      isPipClicked,
      isPipCustomActive,
      isPipNativeActive,
      isSameMatchRouteAndPipNativeActive,
      isWTVEnabled,
      jwt,
      matchId,
      onMatchStreamExport,
      placeholderRef,
      restoreStream,
      setPip,
      startStream,
      stickyOffsetTop,
      streamData,
      type,
    ],
  );

  return <PictureInPictureContext.Provider value={value}>{children}</PictureInPictureContext.Provider>;
};

export default PiPProvider;
